import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Play, Copy, Check, ChevronRight, Terminal,
  BarChart3, Bot, CreditCard, FileText, Globe,
  Wallet, Activity, Zap, Download, Building2,
  Shield, Key, TrendingUp, Share2, Link2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Safe fetch helper (uses XMLHttpRequest to avoid visual-edits intercepting fetch) ── */
function safeFetch(url, opts = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method || 'GET', url);
    if (opts.headers) {
      Object.entries(opts.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }
    xhr.onload = () => {
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { resolve({ response: xhr.responseText, status: xhr.status }); }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(opts.body || null);
  });
}

/* ── Example Definitions ── */
const EXAMPLES = [
  {
    id: 'stats',
    label: 'Protocol Stats',
    icon: BarChart3,
    color: 'blue',
    description: 'Get real-time protocol statistics',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

stats = client.stats()
print(stats)`,
    run: async () => {
      return safeFetch(`${API}/api/agent-credit/stats`);
    },
  },
  {
    id: 'pool',
    label: 'Pool Stats',
    icon: Wallet,
    color: 'green',
    description: 'Credit Marketplace: total credit extended, utilization, fees',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

pool = client.pool_stats()
print(pool)`,
    run: async () => {
      return safeFetch(`${API}/api/agent-credit/pool/stats`);
    },
  },
  {
    id: 'register',
    label: 'Register Agent',
    icon: Bot,
    color: 'purple',
    description: 'Register a new agent in the protocol',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

result = client.register(
    name="AlphaTrader-7",
    capabilities=["trading", "research", "analysis"],
    description="Autonomous DeFi research agent",
    staked_kojiru=25000
)
print(result)`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/agents/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_address: addr,
          name: 'AlphaTrader-7',
          capabilities: ['trading', 'research', 'analysis'],
          description: 'Autonomous DeFi research agent',
          staked_kojiru: 25000,
        }),
      });
    },
  },
  {
    id: 'credit',
    label: 'Apply for Credit',
    icon: CreditCard,
    color: 'orange',
    description: 'Apply for a credit line based on ACS',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

# First register, then apply
credit = client.apply_for_credit()
print(credit)`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/credit/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: addr }),
      });
    },
  },
  {
    id: 'profile',
    label: 'Agent Profile',
    icon: Bot,
    color: 'cyan',
    description: 'Get full agent profile with ACS score',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

profile = client.profile()
print(f"ACS Score: {profile['credit_score']}")
print(f"Tier: {profile['credit_tier']}")`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/agents/${addr}`);
    },
  },
  {
    id: 'knowledge',
    label: 'Knowledge Profile',
    icon: Activity,
    color: 'pink',
    description: 'Agent domains, capabilities, quality scores',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

knowledge = client.knowledge_profile()
print(f"Domains: {knowledge['domains']}")
print(f"Capabilities: {knowledge['capabilities']}")`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/knowledge/agent/${addr}/profile`);
    },
  },
  {
    id: 'domains',
    label: 'List Domains',
    icon: Globe,
    color: 'teal',
    description: 'All domains in the knowledge graph',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

domains = client.domains()
for d in domains["domains"]:
    print(f"{d['domain']}: {d['agent_count']} agents")`,
    run: async () => {
      return safeFetch(`${API}/api/agent-credit/knowledge/domains`);
    },
  },
  {
    id: 'artifacts',
    label: 'My Artifacts',
    icon: FileText,
    color: 'amber',
    description: 'List reasoning artifacts by this agent',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

artifacts = client.artifacts()
for a in artifacts["artifacts"]:
    print(f"[{a['artifact_type']}] {a['ipfs_cid']}")`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/artifacts/agent/${addr}`);
    },
  },
  {
    id: 'repay-status',
    label: 'Repayment Engine',
    icon: Shield,
    color: 'emerald',
    description: 'Global repayment & liquidation stats',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

stats = client._request("GET", "/repayment/stats")
print(f"Repaid: \${stats['repayments']['total_volume']:,.2f}")
print(f"Defaults: {stats['defaults']['count']}")
print(f"Proofs: {stats['proofs_generated']}")`,
    run: async () => {
      return safeFetch(`${API}/api/agent-credit/repayment/stats`);
    },
  },
  {
    id: 'collateral',
    label: 'Collateral Health',
    icon: Shield,
    color: 'blue',
    description: 'Collateral ratio and risk assessment',
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

health = client._request("GET", f"/repayment/agent/{client.agent_address}/collateral-health")
print(f"Ratio: {health['collateral_ratio']}")
print(f"Health: {health['health']}")
print(f"Risk: {health['risk_level']}")`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/repayment/agent/${addr}/collateral-health`);
    },
  },
];

/* ── Institutional Examples (require API Key) ── */
const INSTITUTIONAL_EXAMPLES = [
  {
    id: 'inst-onboard',
    label: 'Onboard Institution',
    icon: Building2,
    color: 'gold',
    description: 'Register and get API credentials',
    requiresKey: false,
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

# Onboard — returns API key (save it!)
result = client._request("POST", "/institutional/onboard", json={
    "institution_name": "Apex Capital",
    "contact_email": "ops@apex.fund",
    "lender_address": "{agent_address}",
    "tier": "professional",
    "initial_deposit": 50000
})
print(f"API Key: {result['api_key']}")
print(f"Tier: {result['tier']}")`,
    run: async (addr) => {
      return safeFetch(`${API}/api/agent-credit/institutional/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          institution_name: 'Apex Capital',
          contact_email: 'ops@apex.fund',
          lender_address: addr,
          tier: 'professional',
          initial_deposit: 50000,
        }),
      });
    },
  },
  {
    id: 'inst-portfolio',
    label: 'Portfolio Analytics',
    icon: TrendingUp,
    color: 'blue',
    description: 'Exposure, yield, risk metrics',
    requiresKey: true,
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}",
    api_key="{api_key}"
)

# Portfolio analytics — deep institutional view
portfolio = client._request("GET", "/institutional/portfolio")
print(f"Balance: \${portfolio['position']['balance']:,.2f}")
print(f"ROI: {portfolio['position']['roi_pct']}%")
print(f"Projected Yield: \${portfolio['risk_metrics']['projected_annual_yield']:,.2f}/yr")`,
    run: async (addr, apiKey) => {
      return safeFetch(`${API}/api/agent-credit/institutional/portfolio`, {
        headers: { 'X-API-Key': apiKey },
      });
    },
  },
  {
    id: 'inst-bulk',
    label: 'Bulk Deposit',
    icon: Wallet,
    color: 'green',
    description: 'Multi-position deposits in one call',
    requiresKey: true,
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}",
    api_key="{api_key}"
)

# Bulk deposit to multiple pool positions
result = client._request("POST", "/institutional/bulk-deposit", json={
    "amounts": [
        {"lender_address": "0xAAA...", "amount": 10000},
        {"lender_address": "0xBBB...", "amount": 25000},
        {"lender_address": "0xCCC...", "amount": 15000},
    ]
})
print(f"Total: \${result['total_deposited']:,.2f}")`,
    run: async (addr, apiKey) => {
      return safeFetch(`${API}/api/agent-credit/institutional/bulk-deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({
          amounts: [
            { lender_address: addr, amount: 10000 },
          ],
        }),
      });
    },
  },
  {
    id: 'inst-priority',
    label: 'Priority Allocation',
    icon: Shield,
    color: 'orange',
    description: 'Control which agents access your capital',
    requiresKey: true,
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}",
    api_key="{api_key}"
)

# Set priority allocation rules
result = client._request("POST", "/institutional/priority-allocation", json={
    "lender_address": "{agent_address}",
    "target_tiers": ["Sovereign", "Autonomous"],
    "max_exposure_per_agent_pct": 1.0,
    "min_acs_score": 650
})
print(f"Effect: {result['effect']}")`,
    run: async (addr, apiKey) => {
      return safeFetch(`${API}/api/agent-credit/institutional/priority-allocation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({
          lender_address: addr,
          target_tiers: ['Sovereign', 'Autonomous'],
          max_exposure_per_agent_pct: 1.0,
          min_acs_score: 650,
        }),
      });
    },
  },
  {
    id: 'inst-tiers',
    label: 'Subscription Tiers',
    icon: CreditCard,
    color: 'purple',
    description: 'View pricing and limits',
    requiresKey: false,
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

# View available institutional tiers
tiers = client._request("GET", "/institutional/tiers")
for name, info in tiers["tiers"].items():
    print(f"{name}: \${info['monthly_fee']}/mo, max \${info['max_deposit']:,}")`,
    run: async () => {
      return safeFetch(`${API}/api/agent-credit/institutional/tiers`);
    },
  },
  {
    id: 'inst-stats',
    label: 'Institutional Stats',
    icon: BarChart3,
    color: 'cyan',
    description: 'Aggregate institutional metrics',
    requiresKey: false,
    python: `from kojiru import KojiruClient

client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="{agent_address}"
)

stats = client._request("GET", "/institutional/stats")
print(f"Institutions: {stats['total_institutions']}")
print(f"Capital: \${stats['total_institutional_capital']:,.2f}")`,
    run: async () => {
      return safeFetch(`${API}/api/agent-credit/institutional/stats`);
    },
  },
];

/* ── Syntax Highlighter (token-based) ── */
function highlightPython(code) {
  const tokens = [];
  const keywords = /\b(from|import|async|await|def|class|return|print|for|in|if|else)\b/;
  const strings = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/;
  const comments = /(#.*)/;
  const numbers = /\b(\d+)\b/;
  const pattern = new RegExp(`(${comments.source}|${strings.source}|${keywords.source}|${numbers.source})`, 'g');

  let last = 0;
  const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const parts = [];
  let match;
  const raw = escaped;
  const re = new RegExp(`(#[^\n]*|"(?:[^"\\\\]|\\\\.)*"|'(?:[^'\\\\]|\\\\.)*'|\\b(?:from|import|async|await|def|class|return|print|for|in|if|else)\\b|\\b\\d+\\b)`, 'g');

  while ((match = re.exec(raw)) !== null) {
    if (match.index > last) parts.push(raw.slice(last, match.index));
    const t = match[0];
    if (t.startsWith('#')) parts.push(`<span class="text-gray-600">${t}</span>`);
    else if (t.startsWith('"') || t.startsWith("'")) parts.push(`<span class="text-emerald-400">${t}</span>`);
    else if (/^(from|import|async|await|def|class|return|print|for|in|if|else)$/.test(t)) parts.push(`<span class="text-purple-400">${t}</span>`);
    else if (/^\d+$/.test(t)) parts.push(`<span class="text-orange-400">${t}</span>`);
    last = re.lastIndex;
  }
  if (last < raw.length) parts.push(raw.slice(last));
  return parts.join('');
}

/* ── Main Component ── */
export default function SDKPlayground() {
  const allExamples = [...EXAMPLES, ...INSTITUTIONAL_EXAMPLES];
  const [selectedExample, setSelectedExample] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const exId = params.get('example');
    if (exId) {
      const found = [...EXAMPLES, ...INSTITUTIONAL_EXAMPLES].find(e => e.id === exId);
      if (found) return found;
    }
    return EXAMPLES[0];
  });
  const [agentAddress, setAgentAddress] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('addr') || '0x742d35Cc6634C0532925a3b844Bc9e7595f2bD18';
  });
  const [apiKey, setApiKey] = useState('');
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sdkCopied, setSdkCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const outputRef = useRef(null);

  const currentCode = selectedExample.python
    .replace(/{agent_address}/g, agentAddress)
    .replace(/{api_key}/g, apiKey || 'kojiru_inst_your_api_key_here');

  const handleRun = async () => {
    if (selectedExample.requiresKey && !apiKey) {
      setOutput({ success: false, data: { error: 'API Key required. Onboard an institution first to get one, or paste your key above.' }, elapsed: 0 });
      return;
    }
    setRunning(true);
    setOutput(null);
    const start = performance.now();
    try {
      const data = await selectedExample.run(agentAddress, apiKey);
      const elapsed = Math.round(performance.now() - start);
      const isError = data?.detail || data?.error || data?.status >= 400;
      setOutput({ success: !isError, data, elapsed });
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setOutput({ success: false, data: { error: err.message }, elapsed });
    }
    setRunning(false);
  };

  useEffect(() => {
    if (output && outputRef.current) {
      outputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [output]);

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const copyCode = () => {
    copyToClipboard(currentCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyInstall = () => {
    copyToClipboard('pip install kojiru');
    setSdkCopied(true);
    setTimeout(() => setSdkCopied(false), 2000);
  };

  const sharePlayground = async () => {
    const base = window.location.origin + window.location.pathname;
    const params = new URLSearchParams({ tab: 'sdk', example: selectedExample.id, addr: agentAddress });
    const url = `${base}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for restricted clipboard contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setShared(true);
    setTimeout(() => setShared(false), 2500);
    window.history.replaceState(null, '', `?${params.toString()}`);
  };

  return (
    <div data-testid="sdk-playground-tab">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Terminal className="w-5 h-5 text-emerald-400" />
              Try the SDK
            </h2>
            <p className="text-sm text-gray-500 mt-1">Run real API calls against the live protocol. No setup required.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyInstall}
              className="flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/10 rounded-lg text-sm font-mono text-gray-300 hover:border-emerald-500/30 hover:text-emerald-400 transition-all"
              data-testid="sdk-copy-install"
            >
              {sdkCopied ? <Check className="w-3.5 h-3.5" /> : <span className="text-emerald-400">$</span>}
              {sdkCopied ? 'Copied!' : 'pip install kojiru'}
            </button>
            <a
              href={`${API}/api/sdk/download`}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400 hover:bg-emerald-500/20 transition-all"
              data-testid="sdk-download-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Download SDK
            </a>
          </div>
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 uppercase mb-1.5 block">Agent Address</label>
            <input
              value={agentAddress}
              onChange={e => setAgentAddress(e.target.value)}
              placeholder="0x..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:border-emerald-500/50 placeholder:text-gray-700"
              data-testid="sdk-agent-address-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase mb-1.5 block flex items-center gap-1.5">
              <Key className="w-3 h-3 text-amber-400" />
              Institutional API Key
              <span className="text-gray-700 normal-case">(for premium endpoints)</span>
            </label>
            <input
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="kojiru_inst_..."
              type="password"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:border-amber-500/50 placeholder:text-gray-700"
              data-testid="sdk-api-key-input"
            />
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left: Example Picker */}
        <div className="col-span-3">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Agent SDK</span>
            </div>
            <div className="p-1.5 space-y-0.5 max-h-[600px] overflow-y-auto">
              {EXAMPLES.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => { setSelectedExample(ex); setOutput(null); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all ${
                    selectedExample.id === ex.id
                      ? 'bg-emerald-500/10 border border-emerald-500/20 text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.03] border border-transparent'
                  }`}
                  data-testid={`sdk-example-${ex.id}`}
                >
                  <ex.icon className={`w-4 h-4 shrink-0 ${selectedExample.id === ex.id ? 'text-emerald-400' : 'text-gray-600'}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{ex.label}</div>
                    <div className="text-xs text-gray-600 truncate">{ex.description}</div>
                  </div>
                  {selectedExample.id === ex.id && <ChevronRight className="w-3.5 h-3.5 text-emerald-400 shrink-0 ml-auto" />}
                </button>
              ))}

              {/* Institutional Section Divider */}
              <div className="flex items-center gap-2 px-3 pt-4 pb-1">
                <Building2 className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs text-amber-500/80 uppercase tracking-wider font-medium">Institutional API</span>
              </div>
              <div className="px-3 pb-2">
                <p className="text-[10px] text-gray-600 leading-relaxed">Premium endpoints for capital providers. Some require an API key.</p>
              </div>

              {INSTITUTIONAL_EXAMPLES.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => { setSelectedExample(ex); setOutput(null); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all ${
                    selectedExample.id === ex.id
                      ? 'bg-amber-500/10 border border-amber-500/20 text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.03] border border-transparent'
                  }`}
                  data-testid={`sdk-example-${ex.id}`}
                >
                  <ex.icon className={`w-4 h-4 shrink-0 ${selectedExample.id === ex.id ? 'text-amber-400' : 'text-gray-600'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {ex.label}
                      {ex.requiresKey && <Key className="w-3 h-3 text-amber-500/60" />}
                    </div>
                    <div className="text-xs text-gray-600 truncate">{ex.description}</div>
                  </div>
                  {selectedExample.id === ex.id && <ChevronRight className="w-3.5 h-3.5 text-amber-400 shrink-0 ml-auto" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Code + Output */}
        <div className="col-span-9 space-y-4">
          {/* Code Editor */}
          <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-white/[0.01]">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                  <div className="w-3 h-3 rounded-full bg-green-500/60" />
                </div>
                <span className="text-xs text-gray-600 font-mono">kojiru_example.py</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={sharePlayground}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-all"
                  data-testid="sdk-share-btn"
                >
                  {shared ? <Check className="w-3.5 h-3.5 text-blue-400" /> : <Link2 className="w-3.5 h-3.5" />}
                  {shared ? 'Link copied!' : 'Share'}
                </button>
                <button
                  onClick={copyCode}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-all"
                  data-testid="sdk-copy-code"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={handleRun}
                  disabled={running}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium transition-all disabled:opacity-50 ${
                    selectedExample.requiresKey && !apiKey
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                      : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                  }`}
                  data-testid="sdk-run-btn"
                >
                  {running ? (
                    <div className="w-3.5 h-3.5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                  ) : selectedExample.requiresKey && !apiKey ? (
                    <Key className="w-3.5 h-3.5" />
                  ) : (
                    <Play className="w-3.5 h-3.5" />
                  )}
                  {running ? 'Running...' : selectedExample.requiresKey && !apiKey ? 'Key Required' : 'Run'}
                </button>
              </div>
            </div>

            {/* Code Body */}
            <div className="p-5 overflow-x-auto">
              <pre className="text-sm font-mono leading-relaxed">
                <code dangerouslySetInnerHTML={{ __html: highlightPython(currentCode) }} />
              </pre>
            </div>
          </div>

          {/* Output Panel */}
          <div ref={outputRef}>
            {running && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                  <span className="text-sm text-gray-400 font-mono">Executing against live protocol...</span>
                </div>
              </motion.div>
            )}

            {output && !running && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`border rounded-xl overflow-hidden ${
                  output.success
                    ? 'bg-emerald-500/[0.03] border-emerald-500/20'
                    : 'bg-red-500/[0.03] border-red-500/20'
                }`}
              >
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${output.success ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <span className="text-xs font-mono text-gray-400">
                      {output.success ? 'Response' : 'Error'} &mdash; {output.elapsed}ms
                    </span>
                  </div>
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                    output.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                  }`}>
                    {output.success ? '200 OK' : 'Error'}
                  </span>
                </div>
                <div className="p-5 overflow-x-auto max-h-80 overflow-y-auto">
                  <pre className="text-sm font-mono text-gray-300 leading-relaxed" data-testid="sdk-output">
                    {JSON.stringify(output.data, null, 2)}
                  </pre>
                </div>
              </motion.div>
            )}

            {!output && !running && (
              <div className="bg-white/[0.02] border border-white/5 border-dashed rounded-xl p-8 text-center">
                <Terminal className="w-8 h-8 text-gray-700 mx-auto mb-3" />
                <p className="text-sm text-gray-600">Click <span className="text-emerald-400 font-medium">Run</span> to execute against the live protocol</p>
                <p className="text-xs text-gray-700 mt-1">Responses come from real on-chain and database queries</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom: SDK Info */}
      <div className="mt-6 bg-white/[0.02] border border-white/5 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white mb-1">Integrate in 3 lines</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              The Kojiru SDK is the invisible credit layer for autonomous agents. One line to draw credit,
              one line to submit work, one line to settle. Works with LangChain, CrewAI, AutoGPT, and any
              Python agent framework. Every transaction accrues verifiable on-chain reputation.
            </p>
            <div className="mt-3 flex gap-3">
              <a href={`${API}/api/sdk/download`} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="sdk-docs-link">
                Download SDK &rarr;
              </a>
              <a href="/agent-credit" onClick={(e) => { e.preventDefault(); }} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
                View API Docs &rarr;
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
